import {useEffect, useRef, useState} from 'react';
import type {ParsedSpec} from '../solidworks-parser';
import type {Part, Stock} from '@app/linecut-core';
import {uid} from '@app/linecut-core';
import {useCalculatorWorker} from '../../worker/useCalculatorWorker';
import {Collapsible} from '../../components/Collapsible';
import {CalcResultView, type MaterialResult} from './CalcResultView';
import {useReportSettings} from './useReportSettings';
import {ExportPanel} from '../export';

interface Props {
  spec: ParsedSpec;
  dealId: string;
  parentFolderId: number | null;
}

// ─── Stock row (UI model) ─────────────────────────────────────────────────────

interface StockRow {
  id: string;
  material: string;
  length: string;
  qty: string;            // '' means ∞ (9999 in the algorithm)
  priority: string;
  _priorityManual: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getLinearMaterials(spec: ParsedSpec): string[] {
  const seen = new Set<string>();
  for (const row of spec.cuttingData) {
    if (row.unit === 'м') seen.add(row.material);
  }
  return Array.from(seen);
}

function buildParts(spec: ParsedSpec, material: string): Part[] {
  return spec.cuttingData
    .filter((r) => r.unit === 'м' && r.material === material)
    .map((r) => {
      const lengthMm = Math.round(r.materialQty * 1000);
      return {
        id: uid(),
        length: lengthMm,
        quantity: r.totalQty,
        name: String(lengthMm),
        material: r.material,
      };
    });
}

function withAutoPriorities(rows: StockRow[]): StockRow[] {
  const materials = [...new Set(rows.map((r) => r.material))];
  const rankById = new Map<string, number>();

  for (const mat of materials) {
    const autoRows = rows
      .filter((r) => r.material === mat && !r._priorityManual)
      .sort((a, b) => (parseFloat(a.length) || 0) - (parseFloat(b.length) || 0));
    autoRows.forEach((r, i) => rankById.set(r.id, i + 1));
  }

  return rows.map((r) => {
    if (r._priorityManual) return r;
    const rank = rankById.get(r.id);
    return rank !== undefined ? { ...r, priority: String(rank) } : r;
  });
}

function makeDefaultRows(materials: string[]): StockRow[] {
  const defaultLengths = [6000, 12000] as const;
  return materials.flatMap((mat) =>
      defaultLengths.map((len, i) => ({
        id: uid(),
        material: mat,
        length: String(len),
        qty: '',
        priority: String(i + 1),
        _priorityManual: false,
      })),
  );
}

const NUM_INPUT = 'w-full text-sm text-right border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400';

// ─── Column style helpers (stocks table) ──────────────────────────────────────

// Numeric columns: tight padding, fixed width
// Material column: fills remaining, 8px gap on each side

function thN(w: number, first = false): React.CSSProperties {
  return { paddingLeft: first ? 12 : 4, paddingRight: 4, paddingTop: 8, paddingBottom: 8, width: w, whiteSpace: 'nowrap' };
}

function tdN(first = false): React.CSSProperties {
  return { paddingLeft: first ? 12 : 4, paddingRight: 4, paddingTop: 6, paddingBottom: 6 };
}

const thMat: React.CSSProperties = { paddingLeft: 8, paddingRight: 8, paddingTop: 8, paddingBottom: 8 };
const tdMat: React.CSSProperties = { paddingLeft: 8, paddingRight: 8, paddingTop: 6, paddingBottom: 6 };

// ─── CuttingCalcPanel ─────────────────────────────────────────────────────────

export function CuttingCalcPanel({ spec, dealId, parentFolderId }: Props) {
  const materials = getLinearMaterials(spec);
  const worker = useCalculatorWorker();
  const { settings, toggleSetting } = useReportSettings();

  const [stockRows, setStockRows] = useState<StockRow[]>(() => makeDefaultRows(materials));
  const [blade, setBlade] = useState('3');
  const [trim, setTrim] = useState('0');

  type CalcState =
    | { kind: 'idle' }
    | { kind: 'running' }
    | { kind: 'done'; results: MaterialResult[]; blade: number; trim: number }
    | { kind: 'error'; message: string };

  const [calcState, setCalcState] = useState<CalcState>({ kind: 'idle' });

  // Auto-run once on mount with default params
  const hasAutoRun = useRef(false);
  useEffect(() => {
    if (materials.length === 0 || hasAutoRun.current) return;
    hasAutoRun.current = true;
    void handleCalculate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (materials.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-400">
        Линейных материалов (ед. изм. «м») в спецификации не найдено — раскрой не требуется.
      </div>
    );
  }

  // ─── Row mutations ───────────────────────────────────────────────────────

  function updateRow(id: string, patch: Partial<Omit<StockRow, 'id'>>) {
    setStockRows((prev) => {
      const updated = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));
      const changedRow = updated.find((r) => r.id === id);
      if ('length' in patch && changedRow && !changedRow._priorityManual) {
        return withAutoPriorities(updated);
      }
      return updated;
    });
  }

  function setPriority(id: string, value: string) {
    setStockRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, priority: value, _priorityManual: true } : r)),
    );
  }

  function addRow() {
    const newRow: StockRow = {
      id: uid(),
      material: materials[0],
      length: '',
      qty: '',
      priority: '',
      _priorityManual: false,
    };
    setStockRows((prev) => withAutoPriorities([...prev, newRow]));
  }

  function removeRow(id: string) {
    setStockRows((prev) => withAutoPriorities(prev.filter((r) => r.id !== id)));
  }

  // ─── Calculate ───────────────────────────────────────────────────────────

  async function handleCalculate() {
    setCalcState({ kind: 'running' });
    try {
      const bladeNum = parseFloat(blade) || 0;
      const trimNum = parseFloat(trim) || 0;

      const validStocks: Stock[] = stockRows
        .filter((r) => r.length && parseFloat(r.length) > 0)
        .map((r) => ({
          id: r.id,
          length: parseFloat(r.length),
          quantity: r.qty === '' ? 9999 : parseInt(r.qty, 10) || 9999,
          material: r.material,
          priority: r.priority === '' ? 1 : parseInt(r.priority, 10) || 1,
        }));

      const results: MaterialResult[] = [];

      for (const material of materials) {
        const matStocks = validStocks.filter((s) => s.material === material);
        const matParts = buildParts(spec, material);
        if (matStocks.length === 0 || matParts.length === 0) continue;

        const result = await worker.calculate(matStocks, matParts, bladeNum, trimNum, '');
        results.push({ material, result });
      }

      setCalcState({ kind: 'done', results, blade: bladeNum, trim: trimNum });
    } catch (e) {
      setCalcState({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <Collapsible title="Заготовки" defaultOpen={true}>
        {/* Stocks table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 28 }} />
              <col style={{ width: 84 }} />
              <col style={{ width: 68 }} />
              <col /* Материал — fills */ />
              <col style={{ width: 60 }} />
              <col style={{ width: 28 }} />
            </colgroup>
            <thead>
              <tr className="text-xs text-gray-400 border-b border-gray-100">
                <th style={thN(28, true)} className="font-medium text-left">#</th>
                <th style={thN(84)} className="font-medium text-right">Длина, мм</th>
                <th style={thN(68)} className="font-medium text-right">Кол-во</th>
                <th style={thMat} className="font-medium text-left">Материал</th>
                <th style={thN(60)} className="font-medium text-right">Приоритет</th>
                <th style={{ ...thN(28), paddingRight: 12 }} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {stockRows.map((row, i) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td style={tdN(true)} className="text-gray-400 text-xs">{i + 1}</td>
                  <td style={tdN()}>
                    <input
                      type="number"
                      min="1"
                      value={row.length}
                      placeholder="0"
                      onChange={(e) => updateRow(row.id, { length: e.target.value })}
                      className={NUM_INPUT}
                    />
                  </td>
                  <td style={tdN()}>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={row.qty}
                      placeholder="∞"
                      onKeyDown={(e) => { if (e.key === '.' || e.key === ',') e.preventDefault(); }}
                      onChange={(e) => updateRow(row.id, { qty: e.target.value })}
                      className={NUM_INPUT}
                    />
                  </td>
                  <td style={tdMat}>
                    <select
                      value={row.material}
                      onChange={(e) => updateRow(row.id, { material: e.target.value })}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 truncate"
                    >
                      {materials.map((mat) => (
                        <option key={mat} value={mat}>{mat}</option>
                      ))}
                    </select>
                  </td>
                  <td style={tdN()}>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={row.priority}
                      placeholder="1"
                      onChange={(e) => setPriority(row.id, e.target.value)}
                      className={NUM_INPUT}
                    />
                  </td>
                  <td style={{ ...tdN(), paddingRight: 12, textAlign: 'center' }}>
                    <button
                      onClick={() => removeRow(row.id)}
                      className="text-gray-300 hover:text-red-400 transition-colors text-base leading-none"
                      title="Удалить"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-2 border-t border-gray-100">
          <button
            onClick={addRow}
            className="text-sm text-red-500 hover:text-red-600 font-medium"
          >
            + Добавить заготовку
          </button>
        </div>

        {/* Blade / trim + calculate */}
        <div className="px-4 py-3 border-t border-gray-100 space-y-3">
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-1.5 text-gray-600">
              Толщина лезвия, мм
              <input
                type="number" min="0" value={blade}
                onChange={(e) => setBlade(e.target.value)}
                className="w-16 text-right border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </label>
            <label className="flex items-center gap-1.5 text-gray-600">
              Торцовка, мм
              <input
                type="number" min="0" value={trim}
                onChange={(e) => setTrim(e.target.value)}
                className="w-16 text-right border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </label>
          </div>

          <button
            onClick={handleCalculate}
            disabled={calcState.kind === 'running'}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-sm font-medium py-2 rounded transition-colors"
          >
            {calcState.kind === 'running' ? 'Расчёт…' : 'Рассчитать раскрой'}
          </button>

          {calcState.kind === 'error' && (
            <p className="text-xs text-red-600">{calcState.message}</p>
          )}
        </div>
      </Collapsible>

      {calcState.kind === 'done' && (
        <>
          <CalcResultView
            results={calcState.results}
            blade={calcState.blade}
            trim={calcState.trim}
            settings={settings}
            onToggle={toggleSetting}
          />
          <ExportPanel
            results={calcState.results}
            spec={spec}
            blade={calcState.blade}
            trim={calcState.trim}
            dealId={dealId}
            parentFolderId={parentFolderId}
            settings={settings}
          />
        </>
      )}
    </div>
  );
}
